import { graphqlRequest } from "./graphql";

export interface BBPSOrderRow {
  amount: string;
  canumber: string;
  operator: string;
  status: string;
  order_id: string;
  whatsub_bbps_operator: { name: string; image: string } | null;
  created_at: string;
  updated_at: string;
}

export interface BBPSOrderFilters {
  userId: string;
  statuses?: string[];
  createdFrom?: string;
  createdTo?: string;
  search?: string;
}

function buildWhere(filters: BBPSOrderFilters): Record<string, unknown> {
  const where: Record<string, unknown> = { user_id: { _eq: filters.userId } };
  if (filters.statuses && filters.statuses.length > 0) {
    where["status"] = { _in: filters.statuses };
  }
  const createdAt: Record<string, string> = {};
  if (filters.createdFrom) createdAt["_gte"] = filters.createdFrom;
  if (filters.createdTo) createdAt["_lte"] = filters.createdTo;
  if (Object.keys(createdAt).length > 0) where["created_at"] = createdAt;
  if (filters.search) {
    where["_or"] = [
      { canumber: { _ilike: `%${filters.search}%` } },
      { whatsub_bbps_operator: { name: { _ilike: `%${filters.search}%` } } },
    ];
  }
  return where;
}

interface GetBBPSOrdersResponse {
  whatsub_bbps: BBPSOrderRow[];
  whatsub_bbps_aggregate: { aggregate: { count: number } };
}

export async function getBBPSOrders(
  filters: BBPSOrderFilters,
  limit: number,
  offset: number,
  token: string
) {
  const query = `
    query GetBBPSOrders($limit: Int!, $offset: Int!, $where: whatsub_bbps_bool_exp) {
      whatsub_bbps(
        order_by: { created_at: desc }
        where: $where
        limit: $limit
        offset: $offset
      ) {
        amount
        canumber
        operator
        status
        order_id
        whatsub_bbps_operator { name image }
        created_at
        updated_at
      }
      whatsub_bbps_aggregate(where: $where) {
        aggregate { count }
      }
    }
  `;
  const data = await graphqlRequest<GetBBPSOrdersResponse>(
    query, { limit, offset, where: buildWhere(filters) }, token
  );
  return { rows: data.whatsub_bbps, total: data.whatsub_bbps_aggregate.aggregate.count };
}

interface GetAllBBPSOrdersResponse {
  whatsub_bbps: BBPSOrderRow[];
}

export async function getAllBBPSOrders(filters: BBPSOrderFilters, token: string) {
  const query = `
    query GetAllBBPSOrders($where: whatsub_bbps_bool_exp) {
      whatsub_bbps(order_by: { created_at: desc }, where: $where) {
        amount canumber operator status order_id
        whatsub_bbps_operator { name image }
        created_at updated_at
      }
    }
  `;
  const data = await graphqlRequest<GetAllBBPSOrdersResponse>(
    query, { where: buildWhere(filters) }, token
  );
  return data.whatsub_bbps;
}

interface GetBBPSStatusesResponse {
  whatsub_bbps: Array<{ status: string }>;
}

export async function getBBPSOrderStatuses(userId: string, token: string) {
  const query = `
    query GetUniqueBBPSStatuses($user_id: uuid!) {
      whatsub_bbps(where: { user_id: { _eq: $user_id } } distinct_on: status) { status }
    }
  `;
  const data = await graphqlRequest<GetBBPSStatusesResponse>(query, { user_id: userId }, token);
  return data.whatsub_bbps.map((s) => s.status);
}
